
import { Component } from 'vue-property-decorator';
import ElementBase from './ElementBase';
import { ClassMap, StyleMap } from '../configuration/ElementConfiguration';
import { ActivityStatusSums, ActivityStatusSumsTree } from '../../backend/backendapi';
import ActivityStatusSumElementConfiguration from '../configuration/ActivityStatusSumElementConfiguration';
import { formatDuration, WindowType } from "../../../util";
import { evaluate } from "../expression";

declare var window: WindowType;

@Component({
  name: 'ActivityStatusSum',
  components: {},
})
export default class ActivityStatusSum extends ElementBase {
  private sums = new ActivityStatusSums();

  private get activityStatusSumConfiguration(): ActivityStatusSumElementConfiguration {
    return this.configuration as ActivityStatusSumElementConfiguration;
  }

  get displayStyle(): string {
    return evaluate(this.activityStatusSumConfiguration.displayStyle, this.bindingsContext) ?? 'stacked';
  }

  get consolidated(): boolean {
    return evaluate(this.activityStatusSumConfiguration.consolidated, this.bindingsContext) === true;
  }

  get status(): string[] {
    return evaluate(this.activityStatusSumConfiguration.status, this.bindingsContext)?.split('+') ?? ['all'];
  }

  get value(): string {
    const values = this.status.map(e => {
      switch (e) {
        case 'age':
          return formatDuration(this.sums[e]);
        default:
          return this.sums[e];
      }
    });
    if (values.some(e => e === undefined)) {
      return '-';
    }
    if (values.length == 1) {
      return values[0];
    }
    return values.reduce((l, r) => l + r, 0);
  }

  get title(): string {
    return this.status.map(e => this.$t('dashboard.activityStatusSums.' + e).toString()).join('+');
  }

  get titleClass(): ClassMap {
    const classes = this.activityStatusSumConfiguration.titleClass ?? {};
    return evaluate(classes, this.bindingsContext);
  }

  get titleStyle(): StyleMap {
    const style = this.activityStatusSumConfiguration.titleStyle ?? {};
    return evaluate(style, this.bindingsContext);
  }

  get valueClass(): ClassMap {
    let classes = Object.assign({}, this.activityStatusSumConfiguration.valueClass ?? {});
    switch (this.displayStyle) {
      case 'buttonWithBadge':
        classes = Object.assign({ 'badge': true, 'badge-light': true }, classes);
        break;
    }
    return evaluate(classes, this.bindingsContext);
  }

  get valueStyle(): StyleMap {
    const style = this.activityStatusSumConfiguration.valueStyle ?? {};
    return evaluate(style, this.bindingsContext);
  }

  get visible(): boolean {//TODO HACK: get rid of this, fire protection permission is not a thing that the dashboard knows, replace with css expression bindings
    if (this.status.includes('fireProtection'))
      return window.application.permissions.misc.fireProtection;
    return true;
  }

  constructor() {
    super();
    this.lastDataUpdate = new Date(0);
    this.timeoutSeconds = 120;
  }

  async mounted(): Promise<void> {
    await super.mounted();
    this.enableTimeout();
  }

  public onInformationPush(elementPath: string, json: string): void {
    super.onInformationPush(elementPath, json);

    if (elementPath !== this.elementPath) {
      return;
    }

    const update = JSON.parse(json);
    Object.entries(update).forEach(([key, value]) => {
      if (this.consolidated && key === 'activityStatusSumsTree') {
        this.sums = ActivityStatusSum.consolidate(value as ActivityStatusSumsTree);
        this.lastDataUpdate = new Date();
      } else if (!this.consolidated && key === 'activityStatusSums') {
        this.sums = value as ActivityStatusSums;
        this.lastDataUpdate = new Date();
      }
    });
  }

  protected defaultClass(): ClassMap {
    let ret = Object.assign({}, super.defaultClass());
    switch (this.displayStyle) {
      case 'buttonWithBadge':
        ret = Object.assign({ 'btn': true, 'btn-secondary': true }, ret);
        break;
      case 'stacked':
        ret = Object.assign({ 'd-inline-block': true, 'text-center': true }, ret);
        break;
    }
    return ret;
  }

  protected get bindingsContext(): any {
    const context = Reflect.get(ElementBase.prototype, 'bindingsContext', this);
    Object.defineProperty(context, 'value', { get: () => this.value });
    return context;
  }

  private static consolidate(entry: ActivityStatusSumsTree): ActivityStatusSums {
    const sums = [{ ...entry.sums }].concat(entry.children.map(e => this.consolidate(e)));
    const ret = new ActivityStatusSums();
    for (let key in sums[0]) {
      ret[key] = sums.map(sum => sum[key]).reduce((l, r) => l + r, 0);
    }
    return ret;
  }
}
